// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { WindowService } from './window.service';
import { Partners } from '../types/partners';

interface IDomainMap {
    [key: string]: IDomain;
}

export type ContextHelpMap = {[key: string]: string};

interface IMapWidgetConfig {
    icon: string;
    width: number;
    height: number;
}

interface IDomain {
    theme: 'light' | 'dark';
    partnerId: Partners;
    showAzureLogin: boolean;
    showOktaLogin: boolean;
    titlePrefix: string;
    translatePostfix: string;
    redirectOnSelectBuyURL: boolean;
    showPaymentMethods: boolean;
    showThemeSelector: boolean;
    showPbaDownload: boolean;
    showFdeDownload: boolean;
    showCliDownload: boolean;
    mapWidget: IMapWidgetConfig;
}

interface IJsonConfig {
    configuration: string;
    sentryEnvironment: string;
    apiUrl: string;
    recaptchaSiteKey: string;
    mapTilesUrl: string;
    domainMap: IDomainMap;
    showTestIntegration: boolean;
    showPbaInstallMenuItem: boolean;
    showPbaWipeMenuItem: boolean;
    showPbaDisableMenuItem: boolean;
    showAgentMenus: boolean;
    showAgentMenusExtended: boolean;
    contextHelpMap: ContextHelpMap;
    defaultHelpUrl: string;
    sentryDsn: string;
}

export const configurationServiceFactory = (configurationService: ConfigurationService) => {
    return () => configurationService.load();
};

@Injectable({providedIn: 'root'})
export class ConfigurationService {
    configuration: string = null;
    sentryEnvironment: string;
    apiUrl: string = null;
    recaptchaSiteKey: string = null;
    mapTilesUrl: string = null;
    domainMap: IDomainMap | null = null;
    theme: 'light' | 'dark' | null = null;
    partnerId: Partners | null = null;
    showAzureLogin: boolean | null = null;
    showOktaLogin: boolean | null = null;
    titlePrefix: string;
    translatePostfix: string;
    redirectOnSelectBuyURL: boolean;
    showPaymentMethods: boolean;
    showThemeSelector: boolean;
    showPbaDownload: boolean;
    showFdeDownload: boolean;
    showCliDownload: boolean;
    showTestIntegration: boolean;
    showPbaInstallMenuItem: boolean;
    showPbaWipeMenuItem: boolean;
    showPbaDisableMenuItem: boolean;
    showAgentMenus: boolean;
    showAgentMenusExtended: boolean;
    contextHelpMap: ContextHelpMap = {};
    mapWidgetConfig: IMapWidgetConfig;
    defaultHelpUrl = 'https://support.cigent.com/support/solutions/73000318756';
    sentryDsn: string;

    constructor(
            private httpClient: HttpClient,
            private windowService: WindowService
        ) { }

    load(): Promise<IJsonConfig>  {
        return this.httpClient.get<IJsonConfig>('/assets/configuration.json')
            .toPromise()
            .then((config: IJsonConfig) => {
                this.configuration = config.configuration || 'production';
                this.sentryEnvironment = config.sentryEnvironment || config.configuration;
                this.apiUrl = config.apiUrl;
                this.recaptchaSiteKey = config.recaptchaSiteKey;
                this.mapTilesUrl = config.mapTilesUrl;
                this.showTestIntegration = config.showTestIntegration;
                this.showPbaInstallMenuItem = config.showPbaInstallMenuItem;
                this.showPbaWipeMenuItem = config.showPbaWipeMenuItem;
                this.showPbaDisableMenuItem = config.showPbaDisableMenuItem;
                this.showAgentMenus = config.showAgentMenus;
                this.showAgentMenusExtended = config.showAgentMenusExtended;
                this.sentryDsn = config.sentryDsn;

                const hostname = this.windowService.getHostName();
                const configKeys = Object.keys(config);

                if (configKeys.includes('defaultHelpUrl')) {
                    this.defaultHelpUrl = config.defaultHelpUrl;
                }

                if (configKeys.includes('contextHelpMap')) {
                    this.contextHelpMap = config.contextHelpMap;
                }

                if (configKeys.includes('domainMap') && Object.keys(config.domainMap).includes(hostname)) {
                    this.theme = config.domainMap[hostname].theme;
                    this.partnerId = config.domainMap[hostname].partnerId;
                    this.showAzureLogin = config.domainMap[hostname].showAzureLogin;
                    this.showOktaLogin = config.domainMap[hostname].showOktaLogin;
                    this.titlePrefix = config.domainMap[hostname].titlePrefix || 'Cigent';
                    this.translatePostfix = config.domainMap[hostname].translatePostfix || '';
                    this.redirectOnSelectBuyURL = config.domainMap[hostname].redirectOnSelectBuyURL;
                    this.showPaymentMethods = config.domainMap[hostname].showPaymentMethods;
                    this.showThemeSelector = config.domainMap[hostname].showThemeSelector;
                    this.showPbaDownload = config.domainMap[hostname].showPbaDownload;
                    this.showFdeDownload = config.domainMap[hostname].showFdeDownload || false;
                    this.showCliDownload = config.domainMap[hostname].showCliDownload;
                    this.mapWidgetConfig = config.domainMap[hostname].mapWidget;
                } else {
                    this.theme = 'light';
                    this.partnerId = Partners.Cigent;
                    this.titlePrefix = 'Cigent';
                    this.translatePostfix = '';
                    this.redirectOnSelectBuyURL = false;
                    this.showPaymentMethods = true;
                    this.showThemeSelector = true;
                    this.mapWidgetConfig = {
                        icon: "assets/cigent-logo.png",
                        width: 48,
                        height: 64
                    };
                }
                return config;
            });
    }
}
